import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Modal from "react-modal";
import Select from "react-select";
import OButton from "components/reusable/OButton";
import ErrorMessage from "components/ErrorMessage";
import { apiPost, apiGet, apiPut } from "utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import { data } from "autoprefixer";
import { bn } from "date-fns/locale";

const AddPriceModal = ({ isOpen, onClose, onSuccess, editData }) => {
  const notification = useToastContext();
  const [products, setProducts] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [areas, setAreas] = useState([]);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const selectedType = watch("type");

  useEffect(() => {
    if (isOpen) {
      apiGet(`${apiPath.ListProduct}?all=true`).then((res) =>
        setProducts(res?.data?.results || [])
      );
      apiGet(`${apiPath.districtList}?limit=1000`).then((res) =>
        setDistricts(res?.data?.results?.docs || [])
      );
      if (editData) {
        reset({
          ...editData,
          date: editData.date
            ? new Date(editData.date).toISOString().slice(0, 10)
            : "",
          product_id: editData.product_id || editData.products,
          districtId: editData.districtData?.districtId || editData.districtId,
          areaId: editData.areaData?.areaId || editData.areaId,
          bn_retail_unit: editData.bn_retail_unit,
          bn_wholesale_unit: editData.bn_wholesale_unit,
        });
        // If there's a district ID, fetch the areas for that district
        if (editData.districtData?.districtId || editData.districtId) {
          apiGet(
            `${apiPath.areaList}?district_id=${
              editData.districtData?.districtId || editData.districtId
            }`
          ).then((res) => setAreas(res?.data?.results || []));
        }
      } else {
        reset();
      }
    }
  }, [isOpen, reset, editData]);

  useEffect(() => {
    const districtId = watch("districtId");
    if (districtId) {
      apiGet(`${apiPath.areaList}?district_id=${districtId}`).then((res) =>
        setAreas(res?.data?.results || [])
      );
    }
  }, [watch("districtId")]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        date: data.date,
        moisture: data.moisture,
        product_id: data.product_id,
        products: data.product_id,
        difference: Number(data.difference),
        info_source_type: data.info_source_type,
        info_source_name: data.info_source_name,
        info_source_number: data.info_source_number,
        info_source_address: data.info_source_address,
        areaId: Number(data.areaId),
        districtId: Number(data.districtId),
        type: data.type,
        retail_spot_price: Number(data.retail_spot_price),
        retail_unit: data.retail_unit,
        bn_retail_unit: data.bn_retail_unit,
        whoelsale_spot_price: Number(data.whoelsale_spot_price),
        wholesale_unit: data.wholesale_unit,
        bn_wholesale_unit: data.bn_wholesale_unit,
      };

      if (editData && editData._id) {
        const result = await apiPut(
          `${apiPath.editPrice}/${editData._id}`,
          payload
        );
        if (result?.data?.success) {
          notification.success(result.data.message);
          onClose();
          onSuccess && onSuccess();
          reset();
        } else {
          notification.error(result.data.message);
        }
      } else {
        const result = await apiPost(apiPath.addPrice, payload);
        if (result?.status === 200) {
          notification.success("Price added successfully");
          // onClose();
          onSuccess && onSuccess();
          // reset();
        } else {
          notification.error("Failed to add price");
        }
      }
    } catch (error) {
      console.error("Error submitting price:", error);
      notification.error("Failed to submit price");
    }
  };

  // Prepare options for react-select
  const productOptions = products.map((p) => ({
    value: p._id,
    label: p.product_name + (p.sub_title ? ` - ${p.sub_title}` : ""),
  }));

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={editData ? "Edit Price" : "Add Price"}
      className="fixed inset-0 flex items-center justify-center z-50"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50"
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="font-medium text-black mb-3 text-xl">
          {editData ? "Edit Price" : "Add Price"}
        </h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Date - full width */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date<span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              {...register("date", { required: "Date is required" })}
              className="w-full border p-2 rounded text-sm"
            />
            <ErrorMessage message={errors?.date?.message} />
          </div>
          {/* Moisture - half width */}
          {/* <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moisture</label>
              <input type="text" placeholder="Moisture" {...register("moisture")} className="w-full border p-2 rounded text-sm" />
            </div>
          </div> */}
          {/* Product - full width */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product<span className="text-red-500">*</span>
            </label>
            <Controller
              name="product_id"
              control={control}
              rules={{ required: "Product is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={products.map((p) => ({
                    value: p._id,
                    label:
                      p.product_name + (p.sub_title ? ` - ${p.sub_title}` : ""),
                  }))}
                  placeholder="Select Product"
                  isClearable
                  classNamePrefix="react-select"
                  value={
                    products
                      .map((p) => ({
                        value: p._id,
                        label:
                          p.product_name +
                          (p.sub_title ? ` - ${p.sub_title}` : ""),
                      }))
                      .find((option) => option.value === field.value) || null
                  }
                  onChange={(option) =>
                    field.onChange(option ? option.value : "")
                  }
                />
              )}
            />
            <ErrorMessage message={errors?.product_id?.message} />
          </div>
          {/* 2-column grid for Type and Spot Price */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type<span className="text-red-500">*</span>
              </label>
              <select
                {...register("type", { required: "Type is required" })}
                className="w-full border p-2 rounded text-sm"
              >
                <option value="">Select Type</option>
                <option value="retail">Retail</option>
                <option value="wholesale">Wholesale</option>
                <option value="both">Both</option>
              </select>
              <ErrorMessage message={errors?.type?.message} />
            </div>
            {(selectedType === "retail" || selectedType === "both") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retail Spot Price
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder="Retail Spot Price"
                  {...register("retail_spot_price")}
                  className="w-full border p-2 rounded text-sm"
                />
              </div>
            )}
            {(selectedType === "wholesale" || selectedType === "both") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Wholesale Spot Price
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder="Wholesale Spot Price"
                  {...register("whoelsale_spot_price")}
                  className="w-full border p-2 rounded text-sm"
                />
              </div>
            )}
          </div>
          {/* 2-column grid for Retail/Wholesale Unit and Info Source Type */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(selectedType === "retail" || selectedType === "both") && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Retail Unit
                  </label>
                  <input
                    type="text"
                    placeholder="Retail Unit"
                    {...register("retail_unit")}
                    className="w-full border p-2 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Retail Unit (BN)
                  </label>
                  <input
                    type="text"
                    placeholder="Retail Unit (BN)"
                    {...register("bn_retail_unit")}
                    className="w-full border p-2 rounded text-sm"
                  />
                </div>
              </>
            )}
            {(selectedType === "wholesale" || selectedType === "both") && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Wholesale Unit
                  </label>
                  <input
                    type="text"
                    placeholder="Wholesale Unit"
                    {...register("wholesale_unit")}
                    className="w-full border p-2 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Wholesale Unit (BN)
                  </label>
                  <input
                    type="text"
                    placeholder="Wholesale Unit (BN)"
                    {...register("bn_wholesale_unit")}
                    className="w-full border p-2 rounded text-sm"
                  />
                </div>
              </>
            )}
          </div>
          {/* Info Source Type - full width */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Info Source Type
            </label>
            <input
              type="text"
              placeholder="Info Source Type"
              {...register("info_source_type")}
              className="w-full border p-2 rounded text-sm"
            />
          </div>
          {/* The rest of the fields remain full width */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Info Source Name
            </label>
            <input
              type="text"
              placeholder="Info Source Name"
              {...register("info_source_name")}
              className="w-full border p-2 rounded text-sm"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Info Source Number<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Info Source Number"
              {...register("info_source_number", {
                required: "Info Source Number is required",
              })}
              className="w-full border p-2 rounded text-sm"
            />
            <ErrorMessage message={errors?.info_source_number?.message} />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Info Source Address
            </label>
            <input
              type="text"
              placeholder="Info Source Address"
              {...register("info_source_address")}
              className="w-full border p-2 rounded text-sm"
            />
          </div>
          {/* 2-column grid for District and Area */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                District<span className="text-red-500">*</span>
              </label>
              <Controller
                name="districtId"
                control={control}
                rules={{ required: "District is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={districts.map((d) => ({
                      value: d.districtId,
                      label: d.name,
                    }))}
                    placeholder="Select District"
                    isClearable
                    classNamePrefix="react-select"
                    value={
                      districts
                        .map((d) => ({
                          value: d.districtId,
                          label: d.name,
                        }))
                        .find((option) => option.value === field.value) || null
                    }
                    onChange={(option) =>
                      field.onChange(option ? option.value : "")
                    }
                  />
                )}
              />
              <ErrorMessage message={errors?.districtId?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area<span className="text-red-500">*</span>
              </label>
              <Controller
                name="areaId"
                control={control}
                rules={{ required: "Area is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={areas.map((a) => ({
                      value: a.areaId,
                      label: a.name,
                    }))}
                    placeholder="Select Area"
                    isClearable
                    classNamePrefix="react-select"
                    value={
                      areas
                        .map((a) => ({
                          value: a.areaId,
                          label: a.name,
                        }))
                        .find((option) => option.value === field.value) || null
                    }
                    onChange={(option) =>
                      field.onChange(option ? option.value : "")
                    }
                    isDisabled={!watch("districtId")}
                  />
                )}
              />
              <ErrorMessage message={errors?.areaId?.message} />
            </div>
          </div>
          <div className="flex justify-end">
            <OButton label={editData ? "Update" : "Add"} type="submit" />
            <button
              type="button"
              onClick={onClose}
              className="ml-4 text-black bg-[#E1E1E1] px-4 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddPriceModal;
